import type { GridFilterModel } from '@mui/x-data-grid-premium';
import { useCallback, useMemo, useState } from 'react';

type UseGridFilterModel = {
  initialState?: GridFilterModel;
  quickFilterPlaceHolder?: string;
};

export const mergeFilterModel = (
  prevFilterModel: GridFilterModel,
  nextFilterModel: Partial<GridFilterModel>,
): GridFilterModel => {
  const items = nextFilterModel.items
    ? [...nextFilterModel.items, ...prevFilterModel.items].reduce(
        (acc, item) => {
          if (acc.some((i) => i.id === item.id && i.field === item.field)) {
            return acc;
          }
          return [...acc, item];
        },
        [] as GridFilterModel['items'],
      )
    : prevFilterModel.items;

  return { ...prevFilterModel, ...nextFilterModel, items };
};

export const useGridFilterModel = (
  { initialState = { items: [] }, quickFilterPlaceHolder = 'Search...' }: UseGridFilterModel = {
    initialState: { items: [] },
    quickFilterPlaceHolder: 'Search...',
  },
) => {
  const [filterModel, setFilterModel] = useState(initialState);
  // An empty box is no filter at all. `''.split(' ')` is `['']` — one blank value, which
  // reads as a filter to everything counting them, so clearing the search leaves Clear
  // Filters on screen with nothing to clear. The grid-owned path already collapses this
  // to `[]`; the page-owned one does not.
  //
  // Only whitespace-only input collapses, so a trailing space mid-word still survives
  // the round trip and isn't eaten out from under the caret.
  const handleSearchValueChange = useCallback((value: string) => {
    setFilterModel((prev) =>
      mergeFilterModel(prev, {
        quickFilterValues: value.trim() ? value.split(' ') : [],
      }),
    );
  }, []);

  const quickFilterProps = useMemo(() => {
    return {
      searchValue: filterModel.quickFilterValues?.join(' ') ?? '',
      handleSearchValueChange,
      placeholder: quickFilterPlaceHolder,
    };
  }, [filterModel, handleSearchValueChange, quickFilterPlaceHolder]);

  return useMemo(
    () => ({
      filterModel,
      setFilterModel,
      quickFilterProps,
    }),
    [filterModel, quickFilterProps],
  );
};
